/**
 * Format a UTC timestamp string for display.
 * - Under 60 minutes: "Xm ago"
 * - Under 24 hours: "Xh ago" or "Xh Ym ago"
 * - Over 24 hours: absolute AEST date/time (UTC+10 fixed, never DST)
 * - Null/undefined: "Never"
 *
 * CRITICAL: relative time uses raw UTC diff only.
 * No timezone conversion for relative calculations.
 * AEST conversion only applied for absolute display (>24h).
 */
export const formatPullTime = (utcString: string | null | undefined): string => {
  if (!utcString) return 'Never';

  // Ensure string is parsed as UTC
  const normalized = utcString.endsWith('Z') ? utcString : utcString + 'Z';
  const utcDate = new Date(normalized);

  if (isNaN(utcDate.getTime())) return 'Invalid date';

  // Relative time: raw UTC diff — NO timezone conversion here
  const nowUtc = new Date();
  const diffMs = nowUtc.getTime() - utcDate.getTime();

  if (diffMs < 0) return 'Just now'; // clock skew safety
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) {
    const remainingMins = diffMinutes % 60;
    return remainingMins > 0 ? `${diffHours}h ${remainingMins}m ago` : `${diffHours}h ago`;
  }

  // Over 24 hours: show absolute AEST (UTC+10 fixed offset — never DST)
  const aestOffsetMs = 10 * 60 * 60 * 1000;
  const aestDate = new Date(utcDate.getTime() + aestOffsetMs);

  const day = aestDate.getUTCDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[aestDate.getUTCMonth()];
  const hours = String(aestDate.getUTCHours()).padStart(2, '0');
  const mins = String(aestDate.getUTCMinutes()).padStart(2, '0');

  if (diffDays < 7) {
    return `${day} ${month} ${hours}:${mins} AEST`;
  }
  return `${day} ${month} ${aestDate.getUTCFullYear()} ${hours}:${mins} AEST`;
};
